import {
  draftFromLevel,
  levelFromDraft,
  parseTableImport,
} from './schema';
import type {
  IconsMatchLevel,
  EditorDraft,
  EditorDraftGoal,
  EditorDraftTile,
} from './schema';

// Internal mutable state — mirrors EditorDraft but uses Map for O(1) tile lookup.
interface InternalState {
  levelId: string;
  columnCount: number;
  traySize: number;
  goals: EditorDraftGoal[];                           // sorted by .order
  columns: { id: string; tileIds: string[] }[];
  tiles: Map<string, { imageId: string; goalId: string | null }>;
  nextTileNum: number;
}

export class EditorView {
  private root: HTMLElement;
  private onChanged: (draft: EditorDraft) => void;
  private state: InternalState;
  private active = false;
  private imageMap: Map<string, string> = new Map(); // imageId → absolute URL
  private imageUrlResolver?: (imageId: string) => string;

  // Selection
  private selectedTileId: string | null = null;
  private selectedGoalId: string | null = null;
  private selectedColId:  string | null = null;

  // Tile picker
  private pickerGoalId: string | null = null;

  // DOM refs (set in buildLayout)
  private goalsListEl!: HTMLElement;
  private columnsListEl!: HTMLElement;
  private tableTextarea!: HTMLTextAreaElement;
  private importErrorEl!: HTMLElement;
  private pickerOverlay!: HTMLElement;
  private pickerListEl!: HTMLElement;

  constructor(container: HTMLElement, onChanged: (draft: EditorDraft) => void) {
    this.root = container;
    this.onChanged = onChanged;
    this.state = this.emptyState();
    this.buildLayout();
    this.bindKeyboard();
  }

  // ── Public API ──────────────────────────────────────────────

  loadLevel(level: IconsMatchLevel): void {
    this.loadDraft(draftFromLevel(level));
  }

  loadDraft(draft: EditorDraft): void {
    const tileMap = new Map<string, { imageId: string; goalId: string | null }>();
    for (const t of draft.tiles) tileMap.set(t.id, { imageId: t.imageId, goalId: t.goalId });

    let maxTile = 0;
    for (const t of draft.tiles) { const n = parseInt(t.id.slice(5)); if (n > maxTile) maxTile = n; }

    this.state = {
      levelId:      draft.levelId,
      columnCount:  draft.columnCount,
      traySize:     draft.traySize,
      goals:        draft.goals.map(g => ({ ...g, tileIds: [...g.tileIds] })).sort((a, b) => a.order - b.order),
      columns:      draft.columns.map(c => ({ id: c.id, tileIds: [...c.tileIds] })),
      tiles:        tileMap,
      nextTileNum:  maxTile + 1,
    };
    this.selectedTileId = null;
    this.selectedGoalId = null;
    this.render();
  }

  getDraft(): EditorDraft {
    const tiles: EditorDraftTile[] = [];
    for (const [id, t] of this.state.tiles) tiles.push({ id, imageId: t.imageId, goalId: t.goalId });
    return {
      levelId:     this.state.levelId,
      columnCount: this.state.columnCount,
      traySize:    this.state.traySize,
      goals:       this.state.goals.map(g => ({ ...g, tileIds: [...g.tileIds] })),
      columns:     this.state.columns.map(c => ({ id: c.id, tileIds: [...c.tileIds] })),
      tiles,
    };
  }

  getLevel(): { ok: true; level: IconsMatchLevel } | { ok: false; errors: string[] } {
    return levelFromDraft(this.getDraft());
  }

  setActive(active: boolean): void {
    this.active = active;
    this.root.style.display = active ? '' : 'none';
  }

  setImageMap(map: Map<string, string>): void {
    this.imageMap = map;
    this.render();
  }

  setImageUrlResolver(fn: (imageId: string) => string): void {
    this.imageUrlResolver = fn;
  }

  private resolveImgUrl(imageId: string): string | undefined {
    if (this.imageMap.has(imageId)) return this.imageMap.get(imageId);
    if (this.imageUrlResolver) {
      const url = this.imageUrlResolver(imageId);
      this.imageMap.set(imageId, url);
      return url;
    }
    return undefined;
  }

  // ── Layout ──────────────────────────────────────────────────

  private buildLayout(): void {
    this.root.innerHTML = `
<div class="icons-editor">
  <section class="editor-section settings-section">
    <div class="ie-settings-row">
      <label class="ie-settings-label" for="ie-tray-size">Tray size</label>
      <input id="ie-tray-size" type="number" class="ie-settings-input" min="1" max="20" value="5" />
      <details class="ie-shortcuts-details">
        <summary class="ie-shortcuts-summary">?</summary>
        <div class="ie-shortcuts-panel">
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Click tile</span><span>Select tile in column</span></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Click col</span><span>Select column (header area)</span></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Click goal</span><span>Select goal card</span></div>
          <div class="ie-shortcuts-row ie-sc-sep"></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">W / S</span><span>Move tile up / down (tile selected)</span></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">A / D</span><span>Move tile left / right (tile selected)</span></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">A / D</span><span>Reorder goal (goal selected)</span></div>
          <div class="ie-shortcuts-row ie-sc-sep"></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Delete</span><span>Remove selected goal / column / tile</span></div>
          <div class="ie-shortcuts-row ie-sc-sep"></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Ctrl+C</span><span>Copy imageId of selected tile</span></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Ctrl+V</span><span>Paste clipboard as imageId</span></div>
          <div class="ie-shortcuts-row ie-sc-sep"></div>
          <div class="ie-shortcuts-row"><span class="ie-sc-key">Dbl-click</span><span>Unassign tile from goal (goal card)</span></div>
        </div>
      </details>
    </div>
  </section>

  <section class="editor-section goals-section">
    <div class="section-header">
      <h3>Goals</h3>
      <button type="button" class="section-add-btn" id="ie-btn-add-goal" title="Add goal">+</button>
    </div>
    <div class="goals-list" id="ie-goals-list"></div>
  </section>

  <section class="editor-section columns-section">
    <div class="section-header">
      <h3>Columns</h3>
      <button type="button" class="section-add-btn" id="ie-btn-add-column" title="Add column">+</button>
    </div>
    <div class="columns-list" id="ie-columns-list"></div>
  </section>

  <section class="editor-section table-section">
    <div class="section-header">
      <h3>Table Import / Export</h3>
    </div>
    <textarea id="ie-table-textarea" class="ie-table-textarea" rows="5" spellcheck="false"
      placeholder="Paste from Google Sheets (column_1&#9;column_2&#9;goal_1&#9;goal_2...)"></textarea>
    <div class="ie-table-actions">
      <button type="button" id="ie-btn-import-table">Import</button>
    </div>
    <div id="ie-import-error" class="ie-import-error"></div>
  </section>
</div>

<div id="ie-picker-overlay" class="ie-picker-overlay hidden">
  <div class="ie-picker-dialog">
    <div class="ie-picker-header">
      <span id="ie-picker-title">Select tile</span>
      <button type="button" id="ie-picker-close">×</button>
    </div>
    <div id="ie-picker-list" class="ie-picker-list"></div>
    <p class="ie-picker-hint">Double-click a tile in a goal to unassign it.</p>
  </div>
</div>`;

    this.goalsListEl    = this.root.querySelector('#ie-goals-list')!;
    this.columnsListEl  = this.root.querySelector('#ie-columns-list')!;
    this.tableTextarea  = this.root.querySelector('#ie-table-textarea')!;
    this.importErrorEl  = this.root.querySelector('#ie-import-error')!;
    this.pickerOverlay  = this.root.querySelector('#ie-picker-overlay')!;
    this.pickerListEl   = this.root.querySelector('#ie-picker-list')!;

    const traySizeInput = this.root.querySelector('#ie-tray-size') as HTMLInputElement;
    traySizeInput.addEventListener('change', () => {
      const val = parseInt(traySizeInput.value);
      if (!isNaN(val) && val >= 1) {
        this.state.traySize = val;
        this.emit();
      } else {
        traySizeInput.value = String(this.state.traySize);
      }
    });

    this.root.querySelector('#ie-btn-add-goal')!
      .addEventListener('click', () => this.addGoal());
    this.root.querySelector('#ie-btn-add-column')!
      .addEventListener('click', () => this.addColumn());
    this.root.querySelector('#ie-picker-close')!
      .addEventListener('click', () => this.closePicker());
    this.root.querySelector('#ie-btn-import-table')!
      .addEventListener('click', () => this.importFromTable());

    // Update table textarea when user focuses it (show current state)
    this.tableTextarea.addEventListener('focus', () => this.updateTableTextarea());

    // Close picker on overlay background click
    this.pickerOverlay.addEventListener('click', (e) => {
      if (e.target === this.pickerOverlay) this.closePicker();
    });
  }

  // ── Rendering ───────────────────────────────────────────────

  private render(): void {
    this.renderGoals();
    this.renderColumns();
    this.updateTableTextarea();
    this.syncTraySizeInput();
  }

  private syncTraySizeInput(): void {
    const el = this.root.querySelector('#ie-tray-size') as HTMLInputElement | null;
    if (el) el.value = String(this.state.traySize);
  }

  private renderGoals(): void {
    const focusedId = (document.activeElement as HTMLElement | null)?.id ?? null;
    this.goalsListEl.innerHTML = '';
    const sorted = [...this.state.goals].sort((a, b) => a.order - b.order);
    for (const goal of sorted) this.goalsListEl.appendChild(this.buildGoalCard(goal));
    if (focusedId) (document.getElementById(focusedId) as HTMLElement | null)?.focus();
  }

  private renderColumns(): void {
    const focusedId = (document.activeElement as HTMLElement | null)?.id ?? null;
    this.columnsListEl.innerHTML = '';
    const neutralIdx = this.buildNeutralIndex();
    for (const col of this.state.columns) this.columnsListEl.appendChild(this.buildColumnCard(col, neutralIdx));
    if (focusedId) (document.getElementById(focusedId) as HTMLElement | null)?.focus();
  }

  // ── Goal card ────────────────────────────────────────────────

  private buildGoalCard(goal: EditorDraftGoal): HTMLElement {
    const card = document.createElement('div');
    card.className = 'ie-goal-card' + (goal.id === this.selectedGoalId ? ' selected' : '');
    card.dataset['goalId'] = goal.id;

    // Header row: id label | order input | remove button
    const header = document.createElement('div');
    header.className = 'ie-goal-header';

    const idLabel = document.createElement('span');
    idLabel.className = 'ie-goal-id';
    idLabel.textContent = goal.id;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'ie-icon-btn';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove goal (unassigns its tiles)';
    removeBtn.addEventListener('click', e => { e.stopPropagation(); this.removeGoal(goal.id); });

    header.append(idLabel, removeBtn);

    // Title input
    const titleInput = document.createElement('input');
    titleInput.type = 'text';
    titleInput.id = `ie-goal-title-${goal.id}`;
    titleInput.className = 'ie-goal-title';
    titleInput.value = goal.title;
    titleInput.placeholder = 'Task title...';
    titleInput.addEventListener('click', e => e.stopPropagation());
    titleInput.addEventListener('input', () => {
      const g = this.state.goals.find(x => x.id === goal.id);
      if (g) g.title = titleInput.value;
      this.emit();
    });

    // Tiles row
    const tilesRow = document.createElement('div');
    tilesRow.className = 'ie-goal-tiles';

    for (const tileId of goal.tileIds) tilesRow.appendChild(this.buildGoalTileSlot(tileId, goal.id));

    const addTileBtn = document.createElement('button');
    addTileBtn.type = 'button';
    addTileBtn.className = 'ie-goal-add-tile';
    addTileBtn.textContent = '+';
    addTileBtn.title = 'Add tile to this goal';
    addTileBtn.addEventListener('click', e => { e.stopPropagation(); this.openPicker(goal.id); });
    tilesRow.appendChild(addTileBtn);

    card.addEventListener('click', () => this.selectGoal(goal.id));
    card.append(header, titleInput, tilesRow);
    return card;
  }

  private buildGoalTileSlot(tileId: string, goalId: string): HTMLElement {
    const tile    = this.state.tiles.get(tileId);
    const goalNum = this.getGoalNumber(goalId);
    const imgUrl  = tile?.imageId ? this.resolveImgUrl(tile.imageId) : undefined;

    const slot = document.createElement('div');
    slot.className = 'ie-goal-tile-slot';
    slot.dataset['tileId'] = tileId;
    slot.title = (tile?.imageId ? tile.imageId + ' — ' : '') + 'Dbl-click to unassign';

    if (imgUrl) {
      const img = document.createElement('img');
      img.src = imgUrl;
      img.className = 'ie-tile-img';
      img.alt = tile!.imageId;
      slot.appendChild(img);
    } else {
      const label = document.createElement('span');
      label.className = 'ie-tile-label';
      label.textContent = String(goalNum);
      slot.appendChild(label);
      if (tile?.imageId) {
        const hint = document.createElement('span');
        hint.className = 'ie-tile-img-hint';
        hint.textContent = tile.imageId;
        slot.appendChild(hint);
      }
    }

    slot.addEventListener('dblclick', e => { e.stopPropagation(); this.unassignTile(tileId); });
    return slot;
  }

  // ── Column card ──────────────────────────────────────────────

  private buildColumnCard(
    col: { id: string; tileIds: string[] },
    neutralIdx: Map<string, number>,
  ): HTMLElement {
    const card = document.createElement('div');
    card.className = 'ie-column-card' + (col.id === this.selectedColId ? ' selected' : '');
    card.dataset['colId'] = col.id;
    card.addEventListener('click', () => this.selectColumn(col.id));

    const colNum = parseInt(col.id.slice(7)) || 0;

    const header = document.createElement('div');
    header.className = 'ie-col-header';

    const idLabel = document.createElement('span');
    idLabel.className = 'ie-col-id';
    idLabel.textContent = `col ${colNum}`;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'ie-icon-btn';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove column and all its tiles';
    removeBtn.addEventListener('click', (e) => { e.stopPropagation(); this.removeColumn(col.id); });

    header.append(idLabel, removeBtn);

    const tilesContainer = document.createElement('div');
    tilesContainer.className = 'ie-col-tiles';

    for (const tileId of col.tileIds) {
      tilesContainer.appendChild(this.buildColumnTile(tileId, neutralIdx));
    }

    const addTileBtn = document.createElement('button');
    addTileBtn.type = 'button';
    addTileBtn.className = 'ie-col-add-tile';
    addTileBtn.textContent = '+';
    addTileBtn.title = 'Add neutral tile to column';
    addTileBtn.addEventListener('click', (e) => { e.stopPropagation(); this.addTileToColumn(col.id); });

    card.append(header, tilesContainer, addTileBtn);
    return card;
  }

  private buildColumnTile(tileId: string, neutralIdx: Map<string, number>): HTMLElement {
    const tile       = this.state.tiles.get(tileId);
    const isSelected = tileId === this.selectedTileId;
    const isNeutral  = !tile || tile.goalId === null;
    const imgUrl     = tile?.imageId ? this.resolveImgUrl(tile.imageId) : undefined;

    const el = document.createElement('div');
    el.className = 'ie-col-tile' +
      (isSelected ? ' selected' : '') +
      (isNeutral  ? ' neutral'  : '');
    el.dataset['tileId'] = tileId;
    el.title = isNeutral
      ? `Neutral tile — click to select, assign to a goal`
      : `Goal ${this.getGoalNumber(tile!.goalId!)}${tile?.imageId ? ' · ' + tile.imageId : ''} — click to select`;

    if (imgUrl) {
      const img = document.createElement('img');
      img.src = imgUrl;
      img.className = 'ie-tile-img';
      img.alt = tile!.imageId;
      el.appendChild(img);
    } else {
      const label = document.createElement('span');
      label.className = 'ie-tile-label';
      label.textContent = isNeutral
        ? `U${neutralIdx.get(tileId) ?? '?'}`
        : String(this.getGoalNumber(tile!.goalId!));
      el.appendChild(label);
    }

    el.addEventListener('click', (e) => { e.stopPropagation(); this.selectTile(tileId); });
    return el;
  }

  // ── Neutral index ────────────────────────────────────────────

  private buildNeutralIndex(): Map<string, number> {
    const idx = new Map<string, number>();
    let n = 1;
    for (const col of this.state.columns) {
      for (const tileId of col.tileIds) {
        const tile = this.state.tiles.get(tileId);
        if (!tile || tile.goalId === null) idx.set(tileId, n++);
      }
    }
    return idx;
  }

  private getGoalNumber(goalId: string): number {
    const sorted = [...this.state.goals].sort((a, b) => a.order - b.order);
    return sorted.findIndex(g => g.id === goalId) + 1;
  }

  // ── Renumbering ─────────────────────────────────────────────

  private renumberGoals(): void {
    const sorted = [...this.state.goals].sort((a, b) => a.order - b.order);
    // Pass 1 — temp IDs to avoid collisions between existing IDs
    sorted.forEach((goal, i) => {
      const tmpId = `__tmp_goal_${i}__`;
      for (const tile of this.state.tiles.values()) {
        if (tile.goalId === goal.id) tile.goalId = tmpId;
      }
      if (this.selectedGoalId === goal.id) this.selectedGoalId = tmpId;
      goal.id = tmpId;
    });
    // Pass 2 — final sequential IDs and normalized orders
    sorted.forEach((goal, i) => {
      const newId = `goal_${i + 1}`;
      for (const tile of this.state.tiles.values()) {
        if (tile.goalId === goal.id) tile.goalId = newId;
      }
      if (this.selectedGoalId === goal.id) this.selectedGoalId = newId;
      goal.id    = newId;
      goal.order = i + 1;
    });
  }

  private renumberColumns(): void {
    // Pass 1 — temp IDs
    this.state.columns.forEach((col, i) => {
      if (this.selectedColId === col.id) this.selectedColId = `__tmp_col_${i}__`;
      col.id = `__tmp_col_${i}__`;
    });
    // Pass 2 — final sequential IDs
    this.state.columns.forEach((col, i) => {
      const newId = `column_${i + 1}`;
      if (this.selectedColId === col.id) this.selectedColId = newId;
      col.id = newId;
    });
    this.state.columnCount = this.state.columns.length;
  }

  // ── State mutations ──────────────────────────────────────────

  private addGoal(): void {
    const maxOrder = this.state.goals.reduce((m, g) => Math.max(m, g.order), 0);
    this.state.goals.push({ id: 'goal_0', order: maxOrder + 1, title: '', tileIds: [] });
    this.renumberGoals();
    this.renderGoals();
    this.emit();
  }

  private removeGoal(goalId: string): void {
    const goal = this.state.goals.find(g => g.id === goalId);
    if (!goal) return;
    for (const tileId of goal.tileIds) {
      const t = this.state.tiles.get(tileId);
      if (t) t.goalId = null;
    }
    this.state.goals = this.state.goals.filter(g => g.id !== goalId);
    if (this.selectedGoalId === goalId) this.selectedGoalId = null;
    this.renumberGoals();
    this.render();
    this.emit();
  }

  private moveGoal(goalId: string, dir: 'left' | 'right'): void {
    const sorted = [...this.state.goals].sort((a, b) => a.order - b.order);
    const idx    = sorted.findIndex(g => g.id === goalId);
    if (idx < 0) return;
    const swapIdx = dir === 'left' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const tmp             = sorted[idx].order;
    sorted[idx].order     = sorted[swapIdx].order;
    sorted[swapIdx].order = tmp;
    this.renumberGoals();
    this.renderGoals();
    this.emit();
  }

  private addColumn(): void {
    this.state.columns.push({ id: 'column_0', tileIds: [] });
    this.renumberColumns();
    this.renderColumns();
    this.emit();
  }

  private removeColumn(colId: string): void {
    const col = this.state.columns.find(c => c.id === colId);
    if (!col) return;
    for (const tileId of col.tileIds) {
      const t = this.state.tiles.get(tileId);
      if (t?.goalId) {
        const goal = this.state.goals.find(g => g.id === t.goalId);
        if (goal) goal.tileIds = goal.tileIds.filter(id => id !== tileId);
      }
      this.state.tiles.delete(tileId);
    }
    this.state.columns = this.state.columns.filter(c => c.id !== colId);
    if (this.selectedColId === colId) this.selectedColId = null;
    if (this.selectedTileId && col.tileIds.includes(this.selectedTileId)) this.selectedTileId = null;
    this.renumberColumns();
    this.render();
    this.emit();
  }

  private addTileToColumn(colId: string): void {
    const col = this.state.columns.find(c => c.id === colId);
    if (!col) return;
    const id = `tile_${this.state.nextTileNum++}`;
    this.state.tiles.set(id, { imageId: '', goalId: null });
    col.tileIds.push(id);
    this.renderColumns();
    this.emit();
  }

  private moveTile(tileId: string, dir: 'up' | 'down' | 'left' | 'right'): void {
    const colIdx = this.state.columns.findIndex(c => c.tileIds.includes(tileId));
    if (colIdx < 0) return;
    const col      = this.state.columns[colIdx];
    const tileIdx  = col.tileIds.indexOf(tileId);

    if (dir === 'up') {
      if (tileIdx <= 0) return;
      [col.tileIds[tileIdx - 1], col.tileIds[tileIdx]] = [col.tileIds[tileIdx], col.tileIds[tileIdx - 1]];
    } else if (dir === 'down') {
      if (tileIdx >= col.tileIds.length - 1) return;
      [col.tileIds[tileIdx], col.tileIds[tileIdx + 1]] = [col.tileIds[tileIdx + 1], col.tileIds[tileIdx]];
    } else {
      const targetIdx = dir === 'left' ? colIdx - 1 : colIdx + 1;
      if (targetIdx < 0 || targetIdx >= this.state.columns.length) return;
      const target    = this.state.columns[targetIdx];
      col.tileIds.splice(tileIdx, 1);
      target.tileIds.splice(Math.min(tileIdx, target.tileIds.length), 0, tileId);
    }
    this.renderColumns();
    this.emit();
  }

  private assignTile(tileId: string, goalId: string): void {
    const tile = this.state.tiles.get(tileId);
    const goal = this.state.goals.find(g => g.id === goalId);
    if (!tile || !goal) return;
    tile.goalId = goalId;
    if (!goal.tileIds.includes(tileId)) goal.tileIds.push(tileId);
    this.render();
    this.emit();
  }

  private unassignTile(tileId: string): void {
    const tile = this.state.tiles.get(tileId);
    if (!tile || !tile.goalId) return;
    const goal = this.state.goals.find(g => g.id === tile.goalId);
    if (goal) goal.tileIds = goal.tileIds.filter(id => id !== tileId);
    tile.goalId = null;
    this.render();
    this.emit();
  }

  // ── Selection ────────────────────────────────────────────────

  private selectTile(tileId: string): void {
    this.selectedTileId = tileId;
    this.selectedGoalId = null;
    this.selectedColId  = null;
    this.renderColumns();
    this.renderGoals();
  }

  private selectGoal(goalId: string): void {
    this.selectedGoalId = goalId;
    this.selectedTileId = null;
    this.selectedColId  = null;
    this.renderGoals();
    this.renderColumns();
  }

  private selectColumn(colId: string): void {
    this.selectedColId  = colId;
    this.selectedTileId = null;
    this.selectedGoalId = null;
    this.renderColumns();
    this.renderGoals();
  }

  private deleteTile(tileId: string): void {
    const tile = this.state.tiles.get(tileId);
    if (tile?.goalId) {
      const goal = this.state.goals.find(g => g.id === tile.goalId);
      if (goal) goal.tileIds = goal.tileIds.filter(id => id !== tileId);
    }
    this.state.tiles.delete(tileId);
    for (const col of this.state.columns) {
      const idx = col.tileIds.indexOf(tileId);
      if (idx >= 0) { col.tileIds.splice(idx, 1); break; }
    }
    if (this.selectedTileId === tileId) this.selectedTileId = null;
    this.render();
    this.emit();
  }

  // ── Tile picker ──────────────────────────────────────────────

  private openPicker(goalId: string): void {
    this.pickerGoalId = goalId;
    const goalNum = this.getGoalNumber(goalId);
    this.root.querySelector('#ie-picker-title')!.textContent = `Add tile to goal_${goalNum}`;

    this.pickerListEl.innerHTML = '';
    const neutralIdx  = this.buildNeutralIndex();
    const neutralTiles = [...this.state.tiles.entries()].filter(([, t]) => t.goalId === null);

    if (neutralTiles.length === 0) {
      const msg = document.createElement('p');
      msg.className = 'ie-picker-empty';
      msg.textContent = 'No unassigned tiles. Add tiles to columns first ("+").';
      this.pickerListEl.appendChild(msg);
    } else {
      for (const [tileId, tile] of neutralTiles) {
        const n      = neutralIdx.get(tileId) ?? 0;
        const imgUrl = tile.imageId ? this.resolveImgUrl(tile.imageId) : undefined;

        const item = document.createElement('div');
        item.className = 'ie-picker-item';

        if (imgUrl) {
          const img = document.createElement('img');
          img.src = imgUrl;
          img.className = 'ie-tile-img';
          img.alt = tile.imageId;
          item.appendChild(img);
        } else {
          const label = document.createElement('span');
          label.className = 'ie-tile-label';
          label.textContent = `U${n}`;
          item.appendChild(label);
          if (tile.imageId) {
            const hint = document.createElement('span');
            hint.className = 'ie-picker-img';
            hint.textContent = tile.imageId;
            item.appendChild(hint);
          }
        }

        item.addEventListener('click', () => {
          if (this.pickerGoalId) this.assignTile(tileId, this.pickerGoalId);
          this.closePicker();
        });
        this.pickerListEl.appendChild(item);
      }
    }

    this.pickerOverlay.classList.remove('hidden');
  }

  private closePicker(): void {
    this.pickerGoalId = null;
    this.pickerOverlay.classList.add('hidden');
  }

  // ── Import / Export ──────────────────────────────────────────

  private updateTableTextarea(): void {
    this.tableTextarea.value = this.serializeEditorState();
  }

  private serializeEditorState(): string {
    const sortedGoals = [...this.state.goals].sort((a, b) => a.order - b.order);
    const maxColRows  = Math.max(...this.state.columns.map(c => c.tileIds.length), 0);
    const maxGoalRows = Math.max(...sortedGoals.map(g => g.tileIds.length), 0);
    const totalRows   = Math.max(maxColRows, maxGoalRows + 1);

    const headers = [
      ...this.state.columns.map(c => c.id),
      ...sortedGoals.map(g => g.id),
    ];

    const rows: string[][] = [headers];

    for (let r = 0; r < totalRows; r++) {
      const row: string[] = [];

      for (const col of this.state.columns) {
        const tileId = col.tileIds[r];
        if (tileId) {
          const t = this.state.tiles.get(tileId);
          row.push(t?.imageId || tileId);
        } else {
          row.push('');
        }
      }

      for (const goal of sortedGoals) {
        if (r === 0) {
          row.push(goal.title || goal.id);
        } else {
          const tileId = goal.tileIds[r - 1];
          if (tileId) {
            const t = this.state.tiles.get(tileId);
            row.push(t?.imageId || tileId);
          } else {
            row.push('');
          }
        }
      }

      rows.push(row);
    }

    return rows.map(r => r.join('\t')).join('\n');
  }

  private importFromTable(): void {
    const tsv = this.tableTextarea.value.trim();
    if (!tsv) return;

    const result = parseTableImport(tsv);
    if (!result.ok) {
      this.importErrorEl.textContent = result.error;
      return;
    }

    this.importErrorEl.textContent = '';
    const draft = { ...this.getDraft(), ...this.draftFromParsed(result.level) };
    this.loadDraft(draft);
    this.emit();
  }

  private draftFromParsed(level: IconsMatchLevel): Omit<EditorDraft, 'levelId'> {
    const tiles = level.goals.flatMap(goal =>
      goal.tiles.map(t => ({ id: t.id, imageId: t.imageId, goalId: goal.id }))
    );
    return {
      columnCount: level.columnCount,
      traySize:    level.traySize,
      goals:       level.goals.map((g, i) => ({
        id:      g.id,
        order:   i + 1,
        title:   g.title,
        tileIds: g.tiles.map(t => t.id),
      })),
      columns: level.columns.map(c => ({ id: c.id, tileIds: [...c.tiles] })),
      tiles,
    };
  }

  // ── Keyboard ─────────────────────────────────────────────────

  private bindKeyboard(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.active) return;

      const isTyping =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement;

      // Ctrl+C — copy imageId of selected tile
      if (e.ctrlKey && e.key === 'c' && this.selectedTileId && !isTyping) {
        e.preventDefault();
        const tile = this.state.tiles.get(this.selectedTileId);
        if (tile?.imageId) navigator.clipboard.writeText(tile.imageId).catch(() => undefined);
        return;
      }

      // Escape — close picker
      if (e.key === 'Escape') {
        this.closePicker();
        return;
      }

      if (isTyping) return;

      const key = e.key.toLowerCase();

      // Delete — remove selected item
      if (e.key === 'Delete') {
        e.preventDefault();
        if (this.selectedGoalId)  { this.removeGoal(this.selectedGoalId);   return; }
        if (this.selectedColId)   { this.removeColumn(this.selectedColId);  return; }
        if (this.selectedTileId)  { this.deleteTile(this.selectedTileId);   return; }
        return;
      }

      // WASD — move selected tile
      if (this.selectedTileId) {
        if (key === 'w') { e.preventDefault(); this.moveTile(this.selectedTileId, 'up');    return; }
        if (key === 's') { e.preventDefault(); this.moveTile(this.selectedTileId, 'down');  return; }
        if (key === 'a') { e.preventDefault(); this.moveTile(this.selectedTileId, 'left');  return; }
        if (key === 'd') { e.preventDefault(); this.moveTile(this.selectedTileId, 'right'); return; }
      }

      // A/D — move selected goal
      if (this.selectedGoalId) {
        if (key === 'a') { e.preventDefault(); this.moveGoal(this.selectedGoalId, 'left');  return; }
        if (key === 'd') { e.preventDefault(); this.moveGoal(this.selectedGoalId, 'right'); return; }
      }
    });

    document.addEventListener('paste', (e) => {
      if (!this.active) return;
      if (!this.selectedTileId) return;
      const isTyping =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement;
      if (isTyping) return;
      const text = e.clipboardData?.getData('text/plain') ?? '';
      if (!text.trim()) return;
      e.preventDefault();
      const tile = this.state.tiles.get(this.selectedTileId);
      if (!tile) return;
      tile.imageId = text.trim();
      this.renderColumns();
      this.renderGoals();
      this.emit();
    });
  }

  // ── Helpers ──────────────────────────────────────────────────

  private emit(): void {
    this.onChanged(this.getDraft());
  }

  private emptyState(): InternalState {
    return {
      levelId:     'level_1',
      columnCount: 5,
      traySize:    5,
      goals:       [],
      columns:     Array.from({ length: 5 }, (_, i) => ({ id: `column_${i + 1}`, tileIds: [] })),
      tiles:       new Map(),
      nextTileNum: 1,
    };
  }
}
